/**
 * A tooltip that works for keyboards, not only mice.
 *
 * Exists chiefly for the collapsed sidebar, where an icon is the whole control
 * and the name has to come from somewhere. CSS-only on purpose: it appears on
 * both :hover and :focus-visible, needs no portal, no positioning library and
 * no state, and it cannot get stuck open. `role="tooltip"` plus
 * `aria-describedby` on the trigger keeps it announced.
 */

import { useId, type ReactNode } from 'react';

export function Tooltip({
  label,
  side = 'right',
  children,
}: {
  label: string;
  /** Where the bubble appears relative to the trigger. */
  side?: 'right' | 'top' | 'bottom';
  /** Exactly one focusable child. */
  children: ReactNode;
}) {
  const id = useId();
  return (
    <span className={`tip tip--${side}`} data-tip-wrap>
      {/* aria-describedby is applied by cloning in CSS-land being impossible;
          instead the child sits inside a describedby-carrying wrapper. */}
      <span aria-describedby={id} className="tip__trigger">
        {children}
      </span>
      <span role="tooltip" id={id} className="tip__bubble">
        {label}
      </span>
    </span>
  );
}
